import { useRef, useState } from "react";
import { api, ApiError } from "../api";
import { ErrorNote } from "../components/ui";
import { useLocations } from "../hooks";
import type { UploadStatus } from "../types";

export default function Upload() {
  const locations = useLocations();
  const [location, setLocation] = useState("");
  const [confirmLoc, setConfirmLoc] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "processing">("idle");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = locations.find((l) => l.code === location);
  const confirmOk = !!selected &&
    [selected.name, selected.code].some((v) => v.toLowerCase() === confirmLoc.trim().toLowerCase());

  const reset = () => {
    setFile(null); setStatus(null); setError(null); setPhase("idle"); setConfirmLoc("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (!file || !location) { setError("Choose a location and a file."); return; }
    if (!confirmOk) { setError(`Type "${selected?.name}" to confirm the location.`); return; }
    setError(null); setStatus(null); setPhase("processing");
    try {
      const s = await api.uploadFile(location, file.name, file);
      setStatus(s);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setPhase("idle");
    }
  };

  return (
    <div>
      <h1 className="h1">Upload</h1>
      <p className="sub">Upload a Spectra export (.xlsx/.csv). The format is detected automatically.
        If the source format changes and an upload fails, paste your data into a standard template below.</p>
      <div className="panel" style={{ marginTop: 0 }}>
        <h2>Standard templates</h2>
        <p className="notice">Keep the header row as-is; paste your rows under it (column case/spacing is flexible).
          <b> Fill the Location column</b> (Chennai / Hyderabad / US) — each row is routed to that office automatically,
          so a Hyderabad row can never land under Chennai. Blank rows use the office selected above.</p>
        <div className="row">
          <a className="primary" style={{ padding: "8px 12px", borderRadius: 10 }}
             href="/templates/AccessIQ-Detailed-Template.xlsx" download>⬇ Detailed (Excel)</a>
          <a className="ghost" style={{ padding: "8px 12px", borderRadius: 10 }}
             href="/templates/AccessIQ-Detailed-Template.csv" download>Detailed (CSV)</a>
          <span style={{ width: 16 }} />
          <a className="primary" style={{ padding: "8px 12px", borderRadius: 10 }}
             href="/templates/AccessIQ-FirstLast-Template.xlsx" download>⬇ First &amp; Last (Excel)</a>
          <a className="ghost" style={{ padding: "8px 12px", borderRadius: 10 }}
             href="/templates/AccessIQ-FirstLast-Template.csv" download>First &amp; Last (CSV)</a>
        </div>
      </div>
      <div className="panel">
        <div className="filters">
          <div className="field">
            <label>Office location</label>
            <select value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">Select…</option>
              {locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Confirm location (type the name)</label>
            <input placeholder={selected ? `Type "${selected.name}"` : "Select a location first"}
                   value={confirmLoc} disabled={!location}
                   onChange={(e) => setConfirmLoc(e.target.value)}
                   style={confirmLoc && !confirmOk ? { borderColor: "var(--incomplete)" } : undefined} />
          </div>
          <div className="field">
            <label>File</label>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv"
                   onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <button className="primary" onClick={submit}
                  disabled={phase !== "idle" || !file || !confirmOk}>
            {phase === "processing" ? "Processing…" : "Upload & process"}
          </button>
          {status && <button className="ghost" onClick={reset}>New upload</button>}
        </div>
        {error && <ErrorNote message={error} />}
      </div>

      {status && (
        <div className="panel">
          <h2>Processing result — {status.status}</h2>
          {status.status === "duplicate_file" && (
            <p className="notice">This exact file was already imported; nothing was double-counted.</p>
          )}
          {status.error_summary && <p className="notice">{status.error_summary}</p>}
          <div className="cards">
            <div className="card"><div className="label">Format</div><div className="value">{status.report_format || "—"}</div></div>
            <div className="card"><div className="label">Accepted</div><div className="value">{status.accepted}</div></div>
            <div className="card"><div className="label">Duplicates</div><div className="value">{status.duplicates}</div></div>
            <div className="card"><div className="label">Rejected</div><div className="value">{status.rejected}</div></div>
            <div className="card"><div className="label">Unmatched</div><div className="value">{status.unmatched_events}</div></div>
            <div className="card"><div className="label">Daily records</div><div className="value">{status.daily_records}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}
